import { useEffect, useState } from 'react'

import { FreeMode, Keyboard, Mousewheel } from 'swiper/modules'
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import './_inlineTimePicker.scss'

type TimeType = 'hour' | 'minute'
type TimeOfDay = 'AM' | 'PM'

interface InlineTimePickerProps {
    getSelectedTime: (hours: number, minutes: number) => void
    selectedDate: Date
}

const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

const to24Hour = (hour: number, timeOfDay: TimeOfDay) => {
    if (timeOfDay === 'AM') {
        return hour === 12 ? 0 : hour
    }

    return hour === 12 ? 12 : hour + 12
}

const getInitialTime = (selectedDate: Date) => {
    const now = new Date()
    const source = isSameDay(selectedDate, now) ? now : selectedDate
    const hours = source.getHours()

    return {
        hour: hours % 12 === 0 ? 12 : hours % 12,
        minutes: source.getMinutes(),
        timeOfDay: hours >= 12 ? 'PM' as TimeOfDay : 'AM' as TimeOfDay
    }
}

export default function InlineTimePicker({
    getSelectedTime,
    selectedDate
}: InlineTimePickerProps): JSX.Element {
    const initialTime = getInitialTime(selectedDate)

    const [hour, setHour] = useState<number>(initialTime.hour)
    const [minutes, setMinutes] = useState<number>(initialTime.minutes)
    const [timeOfDay, setTimeOfDay] = useState<TimeOfDay>(initialTime.timeOfDay)

    const isToday = isSameDay(selectedDate, new Date())

    const isDisabledTime = (
        nextHour: number,
        nextMinutes: number,
        nextTimeOfDay: TimeOfDay
    ) => {
        if (!isToday) {
            return false
        }

        const now = new Date()

        const option = new Date(selectedDate)

        option.setHours(to24Hour(nextHour, nextTimeOfDay))
        option.setMinutes(nextMinutes)
        option.setSeconds(0)
        option.setMilliseconds(0)

        now.setSeconds(0)
        now.setMilliseconds(0)

        return option.getTime() < now.getTime()
    }

    useEffect(() => {
        getSelectedTime(to24Hour(hour, timeOfDay), minutes)
        // intentionally not depending on getSelectedTime to avoid parent/child update loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hour, minutes, timeOfDay])

    const setTime = (type: TimeType, value: number) => {
        const maxValue = type === 'hour' ? 12 : 59
        let temp = value

        if (value < 0) {
            temp = maxValue
        }

        if (value > maxValue) {
            temp = type === 'hour' ? 1 : 0
        }

        if (type === 'hour') {
            if (isDisabledTime(temp, minutes, timeOfDay)) {
                return
            }

            setHour(temp)
            return
        }

        if (isDisabledTime(hour, temp, timeOfDay)) {
            return
        }

        setMinutes(temp)
    }

    const onSlideChangeHandler = (swiper: SwiperClass, type: TimeType) => {
        setTime(type, type === 'hour' ? swiper.realIndex + 1 : swiper.realIndex)
    }

    const handleTimeOfDayChange = (nextTimeOfDay: TimeOfDay) => {
        if (isDisabledTime(hour, minutes, nextTimeOfDay)) {
            return
        }

        setTimeOfDay(nextTimeOfDay)
    }

    return (
        <div className='time-picker-container'>
            <div className='swiper-digit-container'>
                <Swiper
                    modules={[Keyboard, FreeMode, Mousewheel]}
                    mousewheel={true}
                    direction='vertical'
                    centeredSlides={true}
                    slidesPerView={3}
                    height={150}
                    loop={true}
                    initialSlide={hour - 1}
                    onSlideChange={(swiper: SwiperClass) =>
                        onSlideChangeHandler(swiper, 'hour')
                    }
                >
                    {[...Array(12)].map((value, index) => {
                        const displayHour = index + 1
                        const disabled = isDisabledTime(displayHour, minutes, timeOfDay)

                        return (
                            <SwiperSlide key={displayHour}>
                                <span className={disabled ? 'time-picker-disabled' : ''}>
                                    {displayHour}
                                </span>
                            </SwiperSlide>
                        )
                    })}
                </Swiper>
            </div>

            <span className='colon'>:</span>

            <div className='swiper-digit-container'>
                <Swiper
                    modules={[Keyboard, FreeMode, Mousewheel]}
                    mousewheel={true}
                    direction='vertical'
                    centeredSlides={true}
                    slidesPerView={3}
                    height={150}
                    loop={true}
                    initialSlide={minutes}
                    onSlideChange={(swiper: SwiperClass) =>
                        onSlideChangeHandler(swiper, 'minute')
                    }
                >
                    {[...Array(60)].map((value, index) => {
                        const disabled = isDisabledTime(hour, index, timeOfDay)

                        return (
                            <SwiperSlide key={index}>
                                <span className={disabled ? 'time-picker-disabled' : ''}>
                                    {index.toString().length === 1 ? `0${index}` : index}
                                </span>
                            </SwiperSlide>
                        )
                    })}
                </Swiper>
            </div>

            <div className='time-of-day-container'>
                <button
                    className={`time-of-day-button ${
                        timeOfDay === 'AM' ? 'active' : ''
                    } ${
                        isDisabledTime(hour, minutes, 'AM')
                            ? 'time-picker-disabled-button'
                            : ''
                    }`}
                    onClick={() => handleTimeOfDayChange('AM')}
                    disabled={isDisabledTime(hour, minutes, 'AM')}
                >
                    AM
                </button>

                <button
                    className={`time-of-day-button ${
                        timeOfDay === 'PM' ? 'active' : ''
                    } ${
                        isDisabledTime(hour, minutes, 'PM')
                            ? 'time-picker-disabled-button'
                            : ''
                    }`}
                    onClick={() => handleTimeOfDayChange('PM')}
                    disabled={isDisabledTime(hour, minutes, 'PM')}
                >
                    PM
                </button>
            </div>
        </div>
    )
}